import React, {useCallback, useEffect, useRef, useState} from "react";
import {isMobile, isTablet} from "react-device-detect";
import {chatEnterAction} from "./StoreUI";
import axios from "../../../services/axiosInstance";
import {useStoreWebSocket} from "../../../services/StoreWebsocketContext";
import Picker from "@emoji-mart/react";
import {TbPaperclip, TbMoodSmile, TbSend, TbDownload, TbPencil, TbTrash, TbBolt} from "react-icons/tb";
import useVisualViewportVars from "../../../services/visualViewport";
import "../../../css/web/UserPageRenew.css";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사 ↔ 비토리지(관리자) 공용 채팅 보드
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사 대시보드(/store/api/chat) 와 관리자 [상점사] 채팅탭에서 함께 사용한다.
 *
 *  [기존 채팅 대비 개선점]
 *   1) 이미지 : 말풍선에는 가벼운 프리뷰(previewUrl)를 출력하고, 다운로드는 원본(fileUrl)을 받는다.
 *      -> 기존처럼 화질을 낮춘 파일 하나만 저장/다운로드하던 문제 해결
 *   2) 이미지 로드 실패 시 원본으로 자동 폴백하여 말풍선이 깨지지 않는다.
 *   3) 입력창이 내용 길이에 따라 자동으로 늘어난다(최대 6줄).
 *   4) 웹소켓으로 상대방 메세지가 실시간 반영된다.
 *
 *  @param role          '0' 관리자 / '1' 상점사
 *  @param storeSeqNum   대상 상점 식별번호
 *  @param loadMessages  메세지 목록 조회 함수
 *  @param sendMessage   메세지 전송 함수
 *  @param enableMacro   매크로 버튼 사용여부 (관리자만)
 *  @param enableEdit    본인 메세지 수정/삭제 사용여부 (관리자만)
 *  @param onModify      메세지 수정 함수 (관리자)
 *  @param onDelete      메세지 삭제 함수 (관리자)
 *  @param macroList     매크로 목록 [{title, macroDt:[{title, content}]}] (대/소분류 구조 그대로)
 *  @param height        보드 전체 높이 (기본 calc(100vh - 250px))
 *  @param minHeight     최소높이. 모바일 전체화면처럼 화면을 꽉 채울 때는 "0" 을 넘겨야
 *                       디바이스 키보드가 올라왔을 때 입력창이 키보드 아래로 밀리지 않는다.
 *  @param mobile        true 면 디바이스 키보드에 맞춰 화면높이를 보정한다
 */

/*
 * 기기 판정 — 모듈 로드 시 한 번만 한다.
 *  react-device-detect 는 UA 로 정해지는 상수라 리렌더와 무관하고,
 *  App.js 가 PC/모바일 화면을 가르는 기준과 같은 값이다.
 */
const IS_TOUCH_DEVICE = isMobile || isTablet;

function StoreChatBoard({
                            role,
                            storeSeqNum,
                            headerName,
                            loadMessages,
                            sendMessage,
                            enableMacro,
                            enableEdit,
                            onModify,
                            onDelete,
                            macroList,
                            height,
                            minHeight,
                            mobile,
                            onRead,
                        }) {

    // 상점사 전용 웹소켓 (회원/관리자 채팅이 쓰는 커넥션과 분리되어 서로 영향이 없다)
    const {storeConnected, storeSubscribe} = useStoreWebSocket();

    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [emojiOpen, setEmojiOpen] = useState(false);
    const [macroOpen, setMacroOpen] = useState(false);
    /** 펼쳐진 매크로 대분류 index (주문서채팅과 동일하게 하나씩만 펼친다) */
    const [openMacroMst, setOpenMacroMst] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [editText, setEditText] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);

    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);

    // ── 이모티콘 ────────────────────────────────────────────────────────────
    //  기존 회원채팅(UserMyOrderPg / SideQuickMenu)과 동일하게 emoji-mart 피커를 사용한다.
    //  (직접 나열하던 30여개 대신 전체 이모지를 카테고리별로 제공)
    const emojiPopRef = useRef(null);

    /** 피커 바깥을 클릭하면 닫는다 */
    useEffect(() => {
        if (!emojiOpen) return;
        const handleStoreEmojiOutside = (e) => {
            if (e.target.closest && (e.target.closest('em-emoji-picker') || e.target.closest('[data-store-emoji-toggle]'))) return;
            setEmojiOpen(false);
        };
        document.addEventListener('mousedown', handleStoreEmojiOutside);
        return () => document.removeEventListener('mousedown', handleStoreEmojiOutside);
    }, [emojiOpen]);

    // ── 스크롤 ──────────────────────────────────────────────────────────────
    const scrollToBottom = useCallback((smooth) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTo({top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto'});
    }, []);

    /**
     * 지금 바닥 근처를 보고 있는가
     *  예전 대화를 보려고 위로 올려둔 상태에서 새 메세지가 오면
     *  무조건 바닥으로 끌어내려 읽던 위치를 잃어버리는 문제가 있었다.
     *  바닥에서 80px 이내일 때만 따라 내려간다.
     */
    const isNearBottom = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return true;
        return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    }, []);

    /*
     * 디바이스 키보드 대응
     *  키보드가 올라오면 보이는 높이가 줄어드는데, 그만큼 목록도 짧아진다.
     *  바닥을 보고 있었다면 키보드 위로 최신 메세지가 그대로 보이도록 다시 맞춘다.
     *  (위를 읽던 중이면 건드리지 않는다)
     */
    const handleViewportResize = useCallback(() => {
        if (isNearBottom()) scrollToBottom(false);
    }, [isNearBottom, scrollToBottom]);

    useVisualViewportVars(!!mobile, handleViewportResize);

    // ── 조회 ───────────────────────────────────────────────────────────────
    /*
     * [중요] onRead 를 의존성에 넣으면 안 된다.
     *  부모가 onRead 를 인라인 함수로 넘기면 렌더마다 refresh 의 정체성이 바뀌고,
     *  아래 '최초진입' 이펙트가 매 렌더 다시 돌아 force 스크롤이 계속 일어난다.
     *  게다가 refresh -> onRead -> 부모 목록조회 -> 부모 리렌더 -> refresh ... 로
     *  조회와 강제스크롤이 무한히 반복된다. (실제로 이 증상이 있었다)
     *  최신 함수만 ref 로 들고 참조한다.
     */
    const onReadRef = useRef(onRead);
    useEffect(() => {
        onReadRef.current = onRead;
    }, [onRead]);

    /** @param force true 면 스크롤 위치와 무관하게 바닥으로 (최초진입 / 내가 보냈을 때) */
    const refresh = useCallback(async (smooth, force) => {
        // 목록을 갈아끼우기 '전에' 현재 위치를 판단해야 한다
        const stick = force || isNearBottom();
        try {
            const list = await loadMessages();
            setMessages(Array.isArray(list) ? list.filter(m => m.removeFl !== true) : []);
            if (stick) setTimeout(() => scrollToBottom(smooth), 50);
            // 목록조회 시 서버가 읽음처리까지 끝낸다.
            // 좌측 목록의 미확인 수가 곧바로 줄어들도록 부모에게 알린다.
            if (onReadRef.current) onReadRef.current();
        } catch (e) {
            /* 조회 실패 시 기존 목록 유지 */
        }
    }, [loadMessages, scrollToBottom, isNearBottom]);

    // ── 웹소켓 실시간 수신 ──────────────────────────────────────────────────
    //  storeSubscribe 는 '구독 의사' 를 등록하는 함수다.
    //  연결 전에 호출해도 컨텍스트가 연결완료 시점에 알아서 구독하고,
    //  중간에 끊겼다가 재연결되어도 자동으로 다시 구독한다.
    //  따라서 여기서는 연결상태(storeConnected)를 기다릴 필요가 없다.
    //
    //  refresh 를 의존성에 넣으면 loadMessages 가 매 렌더 새로 만들어지는 부모에서
    //  구독/해제가 반복되므로, 최신 refresh 를 ref 로 들고 구독은 상점번호에만 반응시킨다.
    const refreshRef = useRef(refresh);
    useEffect(() => {
        refreshRef.current = refresh;
    }, [refresh]);

    /*
     * 최초진입(= 대화상대가 바뀔 때)에만 최신 메세지부터 보여준다.
     *  의존성을 [refresh] 로 두면 부모가 리렌더될 때마다 바닥으로 끌려 내려간다.
     *  대화상대(storeSeqNum)에만 반응시키고, 함수는 ref 로 최신값을 쓴다.
     */
    useEffect(() => {
        refreshRef.current(false, true);
    }, [storeSeqNum]);

    useEffect(() => {
        if (!storeSeqNum) return;
        const unsubscribeStoreChat = storeSubscribe(
            `store-${storeSeqNum}`,
            `/topic/storeChat/${storeSeqNum}`,
            () => refreshRef.current(true),
        );
        return unsubscribeStoreChat;
    }, [storeSeqNum, storeSubscribe]);

    /** 이모티콘 선택 : 입력창 끝에 붙인다 (피커는 열어둔 채 연속선택 가능) */
    const handleStoreEmojiSelect = (emoji) => {
        handleTextChange(text + (emoji?.native || ''));
    };

    // ── 입력창 자동 높이 ────────────────────────────────────────────────────
    /*
     * 높이 계산을 onChange 안에서 하면 안 된다.
     *  매크로처럼 값을 '코드로' 넣으면 그 시점의 textarea 에는 아직 새 내용이 없어서
     *  scrollHeight 가 이전 값 그대로다. 그래서 몇 줄짜리를 넣어도 한 줄 높이에 머물렀다.
     *  값이 실제로 그려진 뒤(useEffect)에 계산해야 항상 맞는다.
     */
    const handleTextChange = (value) => setText(value);

    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`; // 최대 6줄
    }, [text]);

    // ── 전송 ───────────────────────────────────────────────────────────────
    const handleSend = async () => {
        const content = text.trim();
        if (!content || sending) return;

        setSending(true);
        try {
            await sendMessage({storeSeqNum, chatKey: content, chatRole: role});
            setText('');
            await refresh(true, true); // 내가 보낸 직후에는 항상 바닥으로
        } catch (e) {
            /* 실패 시 입력값 유지 */
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        // 규칙은 StoreUI 의 chatEnterAction 한 곳에 있다
        if (chatEnterAction(e, IS_TOUCH_DEVICE) !== 'send') return;
        e.preventDefault();
        handleSend();
    };

    // ── 파일첨부 ───────────────────────────────────────────────────────────
    const handleFilePick = () => fileInputRef.current?.click();

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || uploading) return;

        // 100MB 제한
        if (file.size > 100 * 1024 * 1024) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post('/storeChat/uploadFile', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            });
            const parsed = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;

            await sendMessage({
                storeSeqNum,
                chatKey: parsed.printFilename,
                fileNm: parsed.originalFilename,
                chatRole: role,
            });
            await refresh(true, true); // 내가 올린 파일은 바로 보여준다
        } catch (err) {
            /* 업로드 실패 */
        } finally {
            setUploading(false);
        }
    };

    // ── 수정/삭제 (관리자 본인 메세지) ──────────────────────────────────────
    const startEdit = (msg) => {
        setEditTarget(msg);
        setEditText(msg.chatKey || '');
    };

    const submitEdit = async () => {
        if (!editTarget || !onModify) return;
        try {
            await onModify(editTarget.seqNum, editText);
            setEditTarget(null);
            setEditText('');
            await refresh(false);
        } catch (e) {
            /* 실패 */
        }
    };

    const submitDelete = async () => {
        if (!deleteTarget || !onDelete) return;
        try {
            await onDelete(deleteTarget.seqNum);
            setDeleteTarget(null);
            await refresh(false);
        } catch (e) {
            /* 실패 */
        }
    };

    // ── 표시 유틸 ──────────────────────────────────────────────────────────
    const isFileMessage = (msg) => typeof msg.chatKey === 'string' && msg.chatKey.startsWith('chatFile');
    const isImageMessage = (msg) => isFileMessage(msg) && /\.(png|jpe?g|gif|webp|bmp)$/i.test(msg.chatKey);
    const isVideoMessage = (msg) => isFileMessage(msg) && /\.(mp4|mov|avi|webm|mkv)$/i.test(msg.chatKey);

    const timeText = (msg) => {
        if (!msg.regTime) return '';
        const t = msg.regTime.split(' ')[1];
        return t ? t.slice(0, 5) : '';
    };

    const downloadUrl = (msg) =>
        `${axios.defaults.baseURL}/storeChat/downloadFile?filename=${encodeURIComponent(msg.chatKey)}` +
        `&originalName=${encodeURIComponent(msg.fileNm || msg.chatKey)}`;

    /** 이미지 프리뷰 로드 실패 시 원본으로 폴백 */
    const onImgError = (e, msg) => {
        if (e.target.dataset.fallback === '1') return;
        e.target.dataset.fallback = '1';
        e.target.src = msg.fileUrl;
    };

    return (
        <div className="up-card" style={{
            display: "flex", flexDirection: "column",
            height: height || "calc(100vh - 250px)",
            // 모바일 전체화면에서는 최소높이를 두면 키보드가 올라왔을 때 입력창이 가려진다
            minHeight: minHeight !== undefined ? minHeight : "440px",
        }}>

            {/*헤더*/}
            <div style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "14px 18px", borderBottom: "1px solid var(--up-border)", background: "var(--up-panel)",
            }}>
                <span style={{
                    width: "8px", height: "8px", borderRadius: "50%",
                    background: storeConnected ? "#7CC98C" : "#C9CDD3",
                }}/>
                <p style={{margin: 0, fontSize: "14px", fontWeight: "bold", color: "var(--up-text)"}}>
                    {headerName || '상점사 채팅'}
                </p>
                <span style={{fontSize: "11.5px", color: "var(--up-text-faint)"}}>
                    {storeConnected ? '실시간 연결됨' : '연결 대기중'}
                </span>
            </div>

            {/*메세지 영역*/}
            {/* minHeight:0 이 있어야 입력창이 길어질 때 목록이 줄어든다 (없으면 입력창이 화면 밖으로 밀린다) */}
            <div ref={scrollRef} data-chat-scroll="1" style={{
                flex: "1 1 auto", minHeight: 0, overflowY: "auto",
                padding: "16px 16px 8px", background: "var(--up-surface-2)",
            }}>
                {messages.length === 0 ?
                    <div style={{textAlign: "center", padding: "60px 0", fontSize: "13px", color: "var(--up-text-faint)"}}>
                        아직 대화내역이 없습니다. 편하게 문의 남겨주세요 :)
                    </div>
                    :
                    messages.map((msg) => {
                        const mine = String(msg.chatRole) === String(role);
                        return (
                            <div key={msg.seqNum}
                                 style={{display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: "12px"}}>
                                <div style={{display: "flex", alignItems: "flex-end", gap: "6px", maxWidth: "78%", flexDirection: mine ? "row-reverse" : "row"}}>

                                    {/*말풍선*/}
                                    <div style={{
                                        padding: isImageMessage(msg) || isVideoMessage(msg) ? "6px" : "10px 14px",
                                        borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                                        background: mine ? "linear-gradient(120deg, #FFC978, #FFB74A)" : "var(--up-surface)",
                                        border: mine ? "none" : "1px solid var(--up-border)",
                                        color: mine ? "#4A3410" : "var(--up-text)",
                                        fontSize: "13.5px", lineHeight: 1.65, wordBreak: "break-word", whiteSpace: "pre-wrap",
                                        boxShadow: "0 2px 8px -6px rgba(0,0,0,0.25)",
                                    }}>
                                        {isImageMessage(msg) ?
                                            <div>
                                                <img src={msg.previewUrl} alt={msg.fileNm}
                                                     onError={(e) => onImgError(e, msg)}
                                                     style={{
                                                         maxWidth: "260px", width: "100%", borderRadius: "10px",
                                                         display: "block", objectFit: "contain",
                                                     }}/>
                                                <a href={downloadUrl(msg)}
                                                   style={{
                                                       display: "inline-flex", alignItems: "center", gap: "4px",
                                                       marginTop: "6px", fontSize: "11.5px",
                                                       color: mine ? "#6B4A14" : "var(--up-brand-2)", textDecoration: "none",
                                                   }}>
                                                    <TbDownload/> 원본 다운로드
                                                </a>
                                            </div>
                                            : isVideoMessage(msg) ?
                                                <div>
                                                    <video src={msg.fileUrl} controls preload="metadata"
                                                           style={{maxWidth: "280px", width: "100%", borderRadius: "10px", display: "block"}}/>
                                                    <a href={downloadUrl(msg)}
                                                       style={{
                                                           display: "inline-flex", alignItems: "center", gap: "4px",
                                                           marginTop: "6px", fontSize: "11.5px",
                                                           color: mine ? "#6B4A14" : "var(--up-brand-2)", textDecoration: "none",
                                                       }}>
                                                        <TbDownload/> 다운로드
                                                    </a>
                                                </div>
                                                : isFileMessage(msg) ?
                                                    <a href={downloadUrl(msg)}
                                                       style={{
                                                           display: "inline-flex", alignItems: "center", gap: "7px",
                                                           color: mine ? "#4A3410" : "var(--up-text)", textDecoration: "none",
                                                       }}>
                                                        <TbDownload style={{fontSize: "17px"}}/>
                                                        <span style={{
                                                            maxWidth: "220px", overflow: "hidden",
                                                            textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                        }}>{msg.fileNm || msg.chatKey}</span>
                                                    </a>
                                                    :
                                                    msg.chatKey
                                        }
                                    </div>

                                    {/*시간 + 수정/삭제*/}
                                    <div style={{display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start", gap: "2px"}}>
                                        {/* 상대가 아직 읽지 않은 내 메세지에만 표시된다.
                                            상대가 읽으면 서버가 읽음신호를 보내 즉시 사라진다. */}
                                        {mine && Number(msg.unreadCount) > 0 ?
                                            <span style={{
                                                fontSize: "10px", fontWeight: "bold", whiteSpace: "nowrap",
                                                padding: "0 5px", borderRadius: "999px", lineHeight: "15px",
                                                background: "rgba(255,190,72,0.22)", color: "#B8790F",
                                            }}>안읽음</span>
                                            : null}
                                        <span style={{fontSize: "10.5px", color: "var(--up-text-faint)", whiteSpace: "nowrap"}}>
                                            {timeText(msg)}
                                        </span>
                                        {/*관리자는 본인이 보낸 메세지만 수정/삭제 가능*/}
                                        {enableEdit && mine ?
                                            <div style={{display: "flex", gap: "4px"}}>
                                                {!isFileMessage(msg) ?
                                                    <TbPencil onClick={() => startEdit(msg)}
                                                              style={{fontSize: "13px", cursor: "pointer", color: "var(--up-text-faint)"}}/>
                                                    : null}
                                                <TbTrash onClick={() => setDeleteTarget(msg)}
                                                         style={{fontSize: "13px", cursor: "pointer", color: "var(--up-text-faint)"}}/>
                                            </div>
                                            : null}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }
            </div>

            {/*입력영역*/}
            <div style={{
                flexShrink: 0,
                borderTop: "1px solid var(--up-border)", background: "var(--up-surface)", padding: "10px 12px 12px",
            }}>

                {/*이모티콘 피커 (회원채팅과 동일한 emoji-mart)*/}
                {emojiOpen ?
                    <div ref={emojiPopRef}
                         style={{display: "flex", justifyContent: "flex-start", marginBottom: "8px"}}>
                        <Picker
                            onEmojiSelect={handleStoreEmojiSelect}
                            previewPosition="none"
                            searchPosition="none"
                            skinTonePosition="none"
                            locale="kr"
                        />
                    </div>
                    : null}

                {/*매크로 (관리자 전용)*/}
                {enableMacro && macroOpen ?
                    <div style={{
                        maxHeight: "260px", overflowY: "auto", padding: "8px", marginBottom: "8px",
                        borderRadius: "10px", background: "var(--up-surface-2)", border: "1px solid var(--up-border)",
                    }}>
                        {(macroList && macroList.length > 0) ?
                            macroList.map((mst, mstIdx) => (
                                <div key={mstIdx}>
                                    {/*큰제목 — 누르면 하위제목이 펼쳐진다 (주문서채팅과 동일한 방식)*/}
                                    <div onClick={() => setOpenMacroMst(openMacroMst === mstIdx ? null : mstIdx)}
                                         style={{
                                             height: "40px", display: "flex", alignItems: "center",
                                             justifyContent: "center", position: "relative", cursor: "pointer",
                                             margin: "5px 0", borderRadius: "8px",
                                             background: "var(--up-panel)",
                                         }}>
                                        <p style={{margin: 0, fontSize: "13px", color: "var(--up-text)"}}>{mst.title}</p>
                                        <span style={{
                                            position: "absolute", right: "12px", fontSize: "11px",
                                            color: "var(--up-text-faint)",
                                            transform: openMacroMst === mstIdx ? "rotate(180deg)" : "none",
                                            transition: "transform .15s",
                                        }}>▼</span>
                                    </div>

                                    {/*하위제목 — 누르면 내용이 입력창에 채워진다*/}
                                    {openMacroMst === mstIdx && (mst.macroDt || []).map((dt, dtIdx) => (
                                        <div key={dtIdx} tabIndex={0}
                                             onClick={() => {
                                                 handleTextChange(dt.content || '');
                                                 setMacroOpen(false);
                                             }}
                                             style={{
                                                 height: "38px", display: "flex", alignItems: "center",
                                                 cursor: "pointer", padding: "0 12px", borderRadius: "8px",
                                             }}>
                                            <span style={{marginRight: "8px", opacity: 0.5, fontSize: "11px"}}>•</span>
                                            <p style={{
                                                margin: 0, fontSize: "12.5px", color: "var(--up-text-sub)",
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                            }}>{dt.title}</p>
                                        </div>
                                    ))}
                                </div>
                            ))
                            :
                            <p style={{margin: 0, padding: "10px", fontSize: "12.5px", color: "var(--up-text-faint)"}}>
                                등록된 매크로가 없습니다
                            </p>
                        }
                    </div>
                    : null}

                <div style={{display: "flex", alignItems: "flex-end", gap: "8px"}}>
                    <textarea ref={textareaRef} className="up-textarea" value={text} rows={1}
                              placeholder="메세지를 입력해주세요"
                              onChange={(e) => handleTextChange(e.target.value)}
                              onKeyDown={onKeyDown}
                              style={{flex: 1, minHeight: "44px", maxHeight: "140px", padding: "11px 14px"}}/>
                    <button className="up-btn-primary" onClick={handleSend} disabled={sending || !text.trim()}
                            style={{height: "44px", width: "52px", flexShrink: 0, fontSize: "18px"}}>
                        {sending ? <span className="up-spin"/> : <TbSend/>}
                    </button>
                </div>

                {/*하단 도구 : 이모티콘 / 파일첨부 / 매크로*/}
                <div style={{display: "flex", alignItems: "center", gap: "6px", marginTop: "8px"}}>
                    <button className="up-btn-ghost" data-store-emoji-toggle="1" onClick={() => setEmojiOpen(v => !v)}
                            style={{height: "32px", padding: "0 10px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px"}}>
                        <TbMoodSmile/> 이모티콘
                    </button>
                    <button className="up-btn-ghost" onClick={handleFilePick} disabled={uploading}
                            style={{height: "32px", padding: "0 10px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px"}}>
                        <TbPaperclip/> {uploading ? '업로드중…' : '파일첨부'}
                    </button>
                    {enableMacro ?
                        <button className="up-btn-ghost" onClick={() => setMacroOpen(v => !v)}
                                style={{height: "32px", padding: "0 10px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px"}}>
                            <TbBolt/> 매크로
                        </button>
                        : null}
                    <input ref={fileInputRef} type="file" onChange={handleFileChange} style={{display: "none"}}/>
                </div>
            </div>

            {/*수정 팝업*/}
            {editTarget ?
                <div style={{
                    position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
                }}>
                    <div className="up-card" style={{width: "100%", maxWidth: "460px", padding: "22px"}}>
                        <p style={{margin: "0 0 12px", fontSize: "15px", fontWeight: "bold", color: "var(--up-text)"}}>메세지 수정</p>
                        <textarea className="up-textarea" value={editText} rows={5}
                                  onChange={(e) => setEditText(e.target.value)}/>
                        <div style={{display: "flex", gap: "8px", marginTop: "16px"}}>
                            <button className="up-btn-ghost" onClick={() => setEditTarget(null)}
                                    style={{flex: 1, height: "42px", fontSize: "13.5px"}}>취소
                            </button>
                            <button className="up-btn-primary" onClick={submitEdit}
                                    style={{flex: 1, height: "42px", fontSize: "13.5px"}}>수정
                            </button>
                        </div>
                    </div>
                </div>
                : null}

            {/*삭제 확인*/}
            {deleteTarget ?
                <div style={{
                    position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
                }}>
                    <div className="up-card" style={{width: "100%", maxWidth: "380px", padding: "24px"}}>
                        <p style={{margin: 0, fontSize: "15px", fontWeight: "bold", color: "var(--up-text)"}}>메세지를 삭제할까요?</p>
                        <p style={{margin: "10px 0 20px", fontSize: "13px", lineHeight: 1.7, color: "var(--up-text-sub)"}}>
                            삭제하면 대화내역과 첨부파일이 함께 완전히 제거됩니다.
                        </p>
                        <div style={{display: "flex", gap: "8px"}}>
                            <button className="up-btn-ghost" onClick={() => setDeleteTarget(null)}
                                    style={{flex: 1, height: "42px", fontSize: "13.5px"}}>취소
                            </button>
                            <button className="up-btn-primary" onClick={submitDelete}
                                    style={{flex: 1, height: "42px", fontSize: "13.5px"}}>삭제
                            </button>
                        </div>
                    </div>
                </div>
                : null}
        </div>
    )
}

export default StoreChatBoard
